import { getOpenV2Dialog } from '../Components/V2/useV2Dialog'

const closingPopups = new WeakSet()

function restoreFocus(owner, target, opener, valid) {
    if (!valid || owner.$?.isUnmounted || Swal.isVisible() || getOpenV2Dialog() !== target) return
    const canRestore = opener?.isConnected && opener !== document.body && opener !== document.documentElement
        && !opener.matches(':disabled') && !opener.closest('[inert],dialog:not([open])')
        && opener.getClientRects().length && getComputedStyle(opener).visibility === 'visible'
    if (canRestore) opener.focus({ preventScroll: true })
    else if (target?.open) target.focus({ preventScroll: true })
    else document.querySelector('main')?.focus({ preventScroll: true })
}

function isolateDialog(target, popup) {
    const siblings = [...(target?.children || [])].filter(element => !element.contains(popup))
        .map(element => [element, element.inert])
    for (const [element] of siblings) element.inert = true
    return () => { for (const [element, inert] of siblings) element.inert = inert }
}

function popupLifecycle(owner, target) {
    const opener = document.activeElement
    let popup = null, valid = true, cleaned = false, restore = () => {}
    const close = () => {
        valid = false
        if (popup && Swal.getPopup() === popup) Swal.close()
    }
    const cleanup = () => {
        if (cleaned) return
        cleaned = true
        target?.removeEventListener('close', close)
        restore()
        if (owner.browserDialogClose === close) owner.browserDialogClose = null
    }
    owner.browserDialogClose = close
    target?.addEventListener('close', close)
    return {
        cleanup, isValid: () => valid && (!target || target.open),
        options: {
            willOpen: element => { popup = element; restore = isolateDialog(target, popup) },
            didOpen: () => { if (target && !target.open) close() },
            willClose: element => closingPopups.add(element),
            didDestroy: cleanup,
            didClose: () => { cleanup(); restoreFocus(owner, target, opener, valid) },
        },
    }
}

const browserDialogs = {
    data() { return { confirmationPending: false, browserDialogClose: null } },
    beforeUnmount() { this.browserDialogClose?.() },
    methods: {
        showBrowserDialog(options) {
            const current = Swal.getPopup()
            if (Swal.isVisible() && !closingPopups.has(current) && !current?.classList.contains('swal2-toast')) {
                return Promise.resolve({ isConfirmed: false, isDismissed: true })
            }
            const target = getOpenV2Dialog()
            const lifecycle = popupLifecycle(this, target)
            let decision
            try {
                decision = Swal.fire({
                    ...options, target: target || 'body', heightAuto: false, returnFocus: false,
                    keydownListenerCapture: true, stopKeydownPropagation: true, ...lifecycle.options,
                })
            } catch (error) { lifecycle.cleanup(); throw error }
            return decision.then(result => lifecycle.isValid() ? result : { ...result, isConfirmed: false })
                .catch(error => { lifecycle.cleanup(); throw error })
        },
        async confirmAction(text, isAllowed = () => true) {
            if (this.confirmationPending || this.$?.isUnmounted || !isAllowed()) return false
            this.confirmationPending = true
            try {
                const result = await this.showBrowserDialog({
                    titleText: 'Confirmar acción', text, icon: 'warning', showCancelButton: true,
                    confirmButtonText: 'Confirmar', cancelButtonText: 'Cancelar', focusCancel: true,
                })
                return result.isConfirmed === true && !this.$?.isUnmounted && isAllowed()
            } catch {
                return false
            } finally {
                this.confirmationPending = false
            }
        },
        showAlert(text, icon = 'warning') {
            return this.showBrowserDialog({ titleText: 'Aviso', text, icon, confirmButtonText: 'Aceptar' })
        },
    },
}

export default browserDialogs
